import { captureException } from "@sentry/react";
import { gridRoutes } from "@/grid-routes";
import { SENTRY_ENABLED } from "@/lib/sentry";

// Increment whenever a breaking change lands in the persisted state shape.
export const CURRENT_SCHEMA_VERSION = 8;

// Each migration transforms state from version N to N+1. Add one here whenever
// CURRENT_SCHEMA_VERSION goes up.
export type PersistedState = Record<string, unknown> & {
	_schemaVersion?: number;
};
type Migration = (state: PersistedState) => PersistedState;

const migrations: Record<number, Migration> = {
	1: (state) => {
		const validGridRouteTypes = new Set(Object.keys(gridRoutes));
		const gridItems = state.gridItems as
			| { tabs: { type: string }[]; activeTabIndex: number }[]
			| undefined;
		if (!Array.isArray(gridItems)) return state;

		const cleaned = gridItems
			.map((item) => {
				const validTabs = item.tabs.filter((tab) =>
					validGridRouteTypes.has(tab.type)
				);
				return {
					...item,
					tabs: validTabs,
					activeTabIndex: Math.min(
						item.activeTabIndex,
						Math.max(0, validTabs.length - 1)
					),
				};
			})
			.filter((item) => item.tabs.length > 0);

		return { ...state, gridItems: cleaned };
	},
	2: (state) => ({ ...state, emojiUsage: {} }),
	3: (state) => {
		if (!state.drafts) state.drafts = new Map();
		return state;
	},
	4: (state) => {
		if (!state.keybinds) {
			state.keybinds = {
				toggleSidebar: { key: "\\", metaKey: true },
				bold: { key: "b", metaKey: true },
				italic: { key: "i", metaKey: true },
				strikethrough: { key: "x", metaKey: true, shiftKey: true },
				inlineCode: { key: "`", metaKey: true },
				link: { key: "k", metaKey: true },
			};
		}
		return state;
	},
	5: (state) => {
		if (state.combinedLayoutMode === "spotlight-broadcast") {
			state.combinedLayoutMode = "tabs";
		}
		return state;
	},
	6: (state) => {
		// Derive the new mode from the old "off" sentinel values.
		const msgWasOff = state.notificationSound === "off";
		const mentionWasOff =
			!state.mentionNotificationSound ||
			state.mentionNotificationSound === "off";

		if (msgWasOff && mentionWasOff) {
			state.notificationSoundMode = "off";
		} else if (msgWasOff) {
			state.notificationSoundMode = "mentions-only";
		} else {
			state.notificationSoundMode = "all";
		}

		if (state.notificationSound === "off" || !state.notificationSound) {
			state.notificationSound = "bloop";
		}
		if (
			state.mentionNotificationSound === "off" ||
			!state.mentionNotificationSound
		) {
			state.mentionNotificationSound = "plink";
		}

		return state;
	},
	7: (state) => {
		// Operations tools moved to the standalone /operations route, so their tab
		// types were removed from gridRoutes. Strip any persisted operationsX tabs
		// (broadcastControls remains valid and is preserved). Same filter as v1.
		const validGridRouteTypes = new Set(Object.keys(gridRoutes));
		const gridItems = state.gridItems as
			| { tabs: { type: string }[]; activeTabIndex: number }[]
			| undefined;
		if (!Array.isArray(gridItems)) return state;

		const cleaned = gridItems
			.map((item) => {
				const validTabs = item.tabs.filter((tab) =>
					validGridRouteTypes.has(tab.type)
				);
				return {
					...item,
					tabs: validTabs,
					activeTabIndex: Math.min(
						item.activeTabIndex,
						Math.max(0, validTabs.length - 1)
					),
				};
			})
			.filter((item) => item.tabs.length > 0);

		return { ...state, gridItems: cleaned };
	},
};

export function migrateState(state: PersistedState): PersistedState {
	const storedVersion = state._schemaVersion;

	if (storedVersion === CURRENT_SCHEMA_VERSION) {
		return state;
	}

	if (storedVersion === undefined) {
		console.warn("[ui-store] No schema version found. Resetting to defaults.");
		return { _schemaVersion: CURRENT_SCHEMA_VERSION };
	}

	if (storedVersion > CURRENT_SCHEMA_VERSION) {
		console.warn(
			`[ui-store] Stored schema version (${storedVersion}) is newer than current (${CURRENT_SCHEMA_VERSION}). Resetting to defaults.`
		);
		return { _schemaVersion: CURRENT_SCHEMA_VERSION };
	}

	let migratedState = { ...state };
	for (let v = storedVersion; v < CURRENT_SCHEMA_VERSION; v++) {
		const migration = migrations[v];
		if (migration) {
			try {
				migratedState = migration(migratedState);
				console.log(`[ui-store] Migrated from version ${v} to ${v + 1}`);
			} catch (error) {
				console.error(`[ui-store] Migration from version ${v} failed:`, error);
				if (SENTRY_ENABLED)
					captureException(error, { tags: { context: "ui_store_migration" } });
				return { _schemaVersion: CURRENT_SCHEMA_VERSION };
			}
		}
	}

	migratedState._schemaVersion = CURRENT_SCHEMA_VERSION;
	return migratedState;
}
